/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { WebSocketCloseCodes, WebSocketStatuses } from './enums';
import { addBaseUrl } from '../../utils/request-helpers';
import { AuthInternalError } from '../../internal-errors';
import { SocketTransportConfig } from './entries';
import { TransportRequestTypes } from '../transport';

import type {
    SocketTransportServiceConfig,
    LiveWebSocketConnection,
    UnsubscribeWebSocket,
    WebSocketSubscriber,
    SocketTransportCommands,
    SocketTransportConnectOptions,
} from './interfaces';

export class SocketTransportService<T = string> {

    protected readonly commands: SocketTransportCommands;
    protected readonly config: SocketTransportConfig;

    protected liveConnections: Record<string, LiveWebSocketConnection<T>> = {};

    constructor(socketTransportServiceConfig: SocketTransportServiceConfig) {
        const {
            commands,
            ...socketTransportConfigParams
        } = socketTransportServiceConfig;

        this.commands = commands;
        this.config = new SocketTransportConfig(socketTransportConfigParams);
    }

    connectToWs(wsName: string, url: string, options: Partial<SocketTransportConnectOptions> = {}): Promise<void> {
        const { noAuth, skipBaseUrl, type = TransportRequestTypes.ToRestServer } = options;

        return this.commands.getAuthToken()
            .then((token: string | null) => {
                if (!noAuth && !token) {
                    return Promise.reject(new AuthInternalError(url));
                }

                const currentLiveConnection: LiveWebSocketConnection<T> = this.getConnection(wsName);

                if (currentLiveConnection && currentLiveConnection.socket && currentLiveConnection.status === WebSocketStatuses.Opened) {
                    return Promise.resolve();
                }

                return new Promise((resolve) => {
                    const urlWithBaseUrl = skipBaseUrl ? url : addBaseUrl(url, this.config.getBaseUrl());
                    const isRest = type === TransportRequestTypes.ToRestServer;
                    const socketEndpoint = isRest ? `${this.config.getRestPrefix()}${url}` : urlWithBaseUrl;
                    const { protocol, origin } = window.location;
                    const requestProtocol = `${protocol}//`;
                    const socketProtocol = protocol === 'https:' ? 'wss://' : 'ws://';
                    const socketUrl = `${socketProtocol}${origin.replace(requestProtocol, '')}${socketEndpoint}`;
                    const socket = new WebSocket(socketUrl, noAuth ? undefined : token ?? '');

                    this.liveConnections[wsName] = {
                        subscriptions: currentLiveConnection?.subscriptions ?? [],
                        status: WebSocketStatuses.Unknown,
                        socket,
                    };

                    socket.onopen = (event: Event) => {
                        resolve();
                        this.onSocketOpen(wsName, { ...options, type }, event);
                    };
                    socket.onmessage = (event: MessageEvent<T>) => this.onSocketData(wsName, { ...options, type }, event);
                    socket.onclose = (event: CloseEvent) => this.onSocketClose(wsName, { ...options, type }, event);
                    socket.onerror = (event: Event) => this.onSocketError(wsName, { ...options, type }, event);
                });
            });
    }

    disconnectWs(wsName: string, closeCode = WebSocketCloseCodes.NormalClosure): void {
        const connection = this.getConnection(wsName);

        if (!this.isConnectionReady(wsName)) {
            this.showNotActiveConnectionError(wsName);
            return;
        }

        connection.socket?.close(closeCode);
        this.removeConnection(wsName);
    }

    sendToWs<R = string>(wsName: string, data: R): void {
        if (this.isConnectionReady(wsName)) {
            (this.getConnection(wsName).socket as WebSocket).send(typeof data === 'string' ? data : JSON.stringify(data));
        } else {
            this.showNotActiveConnectionError(wsName);
            throw Error(`${wsName} connection does not exist and message will not be sent`);
        }
    }

    subscribeToWs(wsName: string, callback: (message: T) => void): UnsubscribeWebSocket {
        const connection = this.getConnection(wsName);
        const subscriberId = Symbol('subscriberId');

        (connection.subscriptions as WebSocketSubscriber<T>[]).push({
            id: subscriberId,
            callback,
        });

        return () => {
            connection.subscriptions = (connection.subscriptions as WebSocketSubscriber<T>[]).filter(({ id }) => (
                id !== subscriberId
            ));
        };
    }

    protected onSocketOpen(wsName: string, { onWebSocketOpen }: SocketTransportConnectOptions, event: Event): void {
        this.setConnectionStatus(wsName, WebSocketStatuses.Opened);
        onWebSocketOpen?.(event);
    }

    protected onSocketData(wsName: string, options: SocketTransportConnectOptions, event: MessageEvent): void {
        if (!this.isConnectionReady(wsName)) {
            console.error(`${wsName} connection is not ready.`);
        }

        const connection = this.getConnection(wsName);

        (connection.subscriptions as WebSocketSubscriber<T>[]).forEach(({ callback }) => {
            callback(event.data);
        });
    }

    protected onSocketClose(wsName: string, { onWebSocketClose }: SocketTransportConnectOptions, event: CloseEvent): void {
        this.setConnectionStatus(wsName, WebSocketStatuses.Closed);
        onWebSocketClose?.(event);
    }

    protected onSocketError(wsName: string, { onWebSocketError }: SocketTransportConnectOptions, event: Event): void {
        this.setConnectionStatus(wsName, WebSocketStatuses.Closed);
        onWebSocketError?.(event);
    }

    protected showNotActiveConnectionError(wsName: string): void {
        console.error(`${wsName} connection does not exist`);
    }

    protected isConnectionReady(wsName: string): boolean {
        return Boolean(
            this.liveConnections[wsName]
            && this.liveConnections[wsName].socket
            && this.liveConnections[wsName].status === WebSocketStatuses.Opened
        );
    }

    protected getConnection(wsName: string): LiveWebSocketConnection<T> {
        if (!this.liveConnections[wsName]) {
            this.liveConnections[wsName] = {
                subscriptions: [],
                status: WebSocketStatuses.Unknown,
                socket: null,
            };
        }

        return this.liveConnections[wsName];
    }

    protected removeConnection(wsName: string): void {
        if (this.liveConnections[wsName]) {
            this.liveConnections[wsName].status = WebSocketStatuses.Closed;
            this.liveConnections[wsName].socket = null;
            this.liveConnections[wsName].subscriptions = [];

            delete this.liveConnections[wsName];
        }
    }

    protected setConnectionStatus(wsName: string, status: WebSocketStatuses): void {
        if (this.liveConnections[wsName]) {
            this.liveConnections[wsName].status = status;
            this.liveConnections[wsName].socket = status === WebSocketStatuses.Closed ? null : this.liveConnections[wsName].socket;
        }
    }

}
